import { action, computed, observable } from 'mobx'
import { type IValidationSchema, required, validationContext } from 'validx'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach, moveItem } from '@/features/legends/editor/collections'
import type { AnswerOptions } from '@/features/legends/editor/entities'
import { Types } from '@/features/legends/editor/ryno-types'
import type { EffectEditorViewModel } from '@/features/legends/effects/EffectEditorViewModel'
import {
  type EffectDescriptor,
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
  type TriggerWhenDescriptor,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { SelectQuestion } from '@/features/legends/questions/questions'
import { validateElements, validateObject } from '@/utils/validx-util'
import { SelectOptionEditorViewModel } from './SelectOptionEditorViewModel'

/**
 * A question answered by picking one option, or several.
 */
export class SelectQuestionEditorViewModel extends QuestionEditorViewModelBase<SelectQuestion> {
  private readonly _schema: IValidationSchema<SelectQuestionEditorViewModel> = {
    ...QuestionEditorViewModelBase.sharedSchema,
    effect: [validateObject('There are invalid effects')],
    question: [required('Question is required')],
    options: [validateElements('One or more options are invalid')],
  }

  readonly type = 'Select'

  /**
   * Runs for multi selects, before the selected options' effects.
   */
  readonly effect: EffectEditorViewModel

  @observable accessor question = 'New Select Question'
  @observable accessor description = ''
  readonly options = observable.array<SelectOptionEditorViewModel>([], {
    deep: false,
  })

  /**
   * The option added last, which focuses its text field.
   */
  @observable.ref accessor addedOption: SelectOptionEditorViewModel | null =
    null
  @observable accessor multi = false

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this.effect = this.createEffect()
    this.lifecycle.manage(this.effect)
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.options))
    this._validateWhileEditing(this._schema)
  }

  /** @inheritDoc */
  @computed
  get valueType() {
    return this.multi ? Types.list(Types.string) : Types.string
  }

  /** @inheritDoc */
  get displayText() {
    return this.question
  }

  /** @inheritDoc */
  @computed
  get answerOptions(): AnswerOptions {
    return {
      type: 'select',
      options: this.options.map((o) => ({
        id: String(o.id),
        value: o.id,
        text: o.text,
      })),
    }
  }

  /** @inheritDoc */
  @computed
  get effectDescriptors(): EffectDescriptor[] {
    return [
      ...(this.multi
        ? [
            {
              id: this.id,
              title: `${this.id} (will run before option effects)`,
              effect: this.effect,
            },
          ]
        : []),
      ...this.options.map((o) => ({
        id: o.cid,
        title: o.text || String(o.id) || '(untitled)',
        effect: o.effect,
      })),
    ]
  }

  /** @inheritDoc */
  @computed
  get triggerWhenDescriptors(): TriggerWhenDescriptor[] {
    return this.options.map((o) => ({
      id: o.cid,
      title: o.text || String(o.id) || '(untitled)',
      triggerWhen: o.triggerWhen,
    }))
  }

  /**
   * Sets whether the client can pick more than one option.
   */
  @action.bound
  setMulti(value: boolean) {
    this.multi = value
  }

  /**
   * Adds an empty option at the end.
   */
  @action.bound
  addOption() {
    this.addedOption = new SelectOptionEditorViewModel(this.dependencies, this)
    this.options.push(this.addedOption)
  }

  /**
   * Removes the option.
   */
  @action.bound
  removeOption(option: SelectOptionEditorViewModel) {
    this.options.remove(option)
  }

  /**
   * Moves an option, for drag-to-reorder.
   */
  @action.bound
  moveByIndex(oldIndex: number, newIndex: number) {
    moveItem(this.options, oldIndex, newIndex)
  }

  /** @inheritDoc */
  @action
  hydrate(question: SelectQuestion) {
    this._hydrateShared(question)
    this.question = question.question ?? this.question
    this.description = question.description ?? ''
    this.multi = !!question.multi
    this.effect.hydrate(question.effect)
    if (question.options) {
      this.options.replace(
        question.options.map((data) => {
          const option = new SelectOptionEditorViewModel(
            this.dependencies,
            this,
          )
          option.hydrate(data)
          return option
        }),
      )
    }
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor.inner?.options,
      'options',
      'There are invalid options',
    )
    super.receiveErrors(descriptor)
  }

  /** @inheritDoc */
  protected _serialize(): SelectQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      question: this.question,
      description: this.description,
      options: this.options.map((o) => o.toJSON()),
      multi: this.multi,
      effect: this.effect.toJSON(),
    }
  }
}
